<template>
  <section class="match-stage">
    <div class="match-card">
      <div class="scan-wrap">
        <div class="scan-ring r1" />
        <div class="scan-ring r2" />
        <div class="scan-core">
          <span class="core-icon">◎</span>
        </div>
        <div class="scan-sweep" />
      </div>

      <p class="status-line">{{ label }}</p>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progress + '%' }" />
      </div>

      <ul class="phase-steps">
        <li
          v-for="(step, i) in steps"
          :key="step.key"
          :class="{ done: i < stepIndex, active: i === stepIndex }"
        >
          <span class="step-dot" />
          {{ step.short }}
        </li>
      </ul>

      <div v-if="server" class="net-strip">
        <span>接入 {{ server }}</span>
        <span v-if="pingMs != null" class="ping">{{ pingMs }} ms</span>
        <span class="enc">TLS · wss</span>
      </div>

      <div v-if="opponent" class="duel">
        <div class="duel-player you">
          <div class="duel-avatar you-bg">{{ youInitial }}</div>
          <div class="duel-meta">
            <span class="duel-name">{{ youName }}</span>
            <span class="duel-rating">{{ youRating }}</span>
          </div>
        </div>
        <div class="duel-vs">
          <span>VS</span>
          <small>Rating 匹配</small>
        </div>
        <div class="duel-player opp">
          <div
            class="duel-avatar"
            :style="{ background: opponent.avatarColor }"
          >
            {{ oppInitial }}
          </div>
          <div class="duel-meta">
            <span class="duel-name">{{ opponent.name }}</span>
            <span class="duel-rating">{{ opponent.rating }}</span>
            <span class="duel-region">{{ opponent.region }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'MatchingView',
  props: {
    label: { type: String, default: '连接中…' },
    server: { type: String, default: '' },
    pingMs: { type: Number, default: null },
    stepIndex: { type: Number, default: 0 },
    progress: { type: Number, default: 12 },
    youName: { type: String, required: true },
    youRating: { type: Number, required: true },
    opponent: { type: Object, default: null },
    steps: {
      type: Array,
      default: () => [
        { key: 'connect', short: '连接' },
        { key: 'auth', short: '鉴权' },
        { key: 'queue', short: '队列' },
        { key: 'found', short: '匹配' },
        { key: 'ready', short: '就绪' },
      ],
    },
  },
  computed: {
    youInitial() {
      return (this.youName || '我').slice(0, 1).toUpperCase()
    },
    oppInitial() {
      return (this.opponent?.name || '?').slice(0, 1).toUpperCase()
    },
  },
}
</script>

<style scoped>
.match-stage {
  display: flex;
  justify-content: center;
  padding: 1rem 0 2rem;
}

.match-card {
  width: 100%;
  max-width: 520px;
  padding: 2.25rem 2rem 2rem;
  border-radius: 24px;
  background: linear-gradient(165deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98));
  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 32px 64px rgba(0, 0, 0, 0.45);
}

.scan-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 1.75rem;
}

.scan-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(99, 102, 241, 0.35);
}

.scan-ring.r2 {
  inset: 14px;
  border-color: rgba(16, 185, 129, 0.4);
  animation: spin 8s linear infinite reverse;
}

.scan-ring.r1 {
  animation: spin 6s linear infinite;
}

.scan-core {
  position: absolute;
  inset: 32px;
  border-radius: 50%;
  background: radial-gradient(circle, #1e293b, #0f172a);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.core-icon {
  font-size: 1.75rem;
  color: #a5b4fc;
}

.scan-sweep {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    rgba(99, 102, 241, 0.35) 40deg,
    transparent 80deg
  );
  animation: spin 2.2s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.status-line {
  text-align: center;
  font-size: 1.05rem;
  font-weight: 500;
  margin: 0 0 1rem;
  min-height: 1.5em;
  color: #e2e8f0;
}

.progress-track {
  height: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
  margin-bottom: 1.25rem;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #6366f1, #10b981);
  transition: width 0.45s ease;
}

.phase-steps {
  list-style: none;
  margin: 0 0 1.25rem;
  padding: 0;
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: #64748b;
}

.phase-steps li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #334155;
  transition: background 0.3s, box-shadow 0.3s;
}

.phase-steps li.done .step-dot {
  background: #10b981;
}

.phase-steps li.active .step-dot {
  background: #6366f1;
  box-shadow: 0 0 10px rgba(99, 102, 241, 0.8);
}

.phase-steps li.active {
  color: #c7d2fe;
  font-weight: 600;
}

.net-strip {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.72rem;
  color: #64748b;
  margin-bottom: 1.5rem;
}

.ping {
  color: #6ee7b7;
}

.enc {
  color: #475569;
}

.duel {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0.75rem;
  align-items: center;
  padding: 1rem;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.06);
  animation: fade-up 0.5s ease;
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.duel-player {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.duel-avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: #fff;
  font-size: 1.1rem;
}

.duel-avatar.you-bg {
  background: linear-gradient(135deg, #10b981, #059669);
}

.duel-name {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.duel-rating {
  font-size: 0.75rem;
  color: #94a3b8;
}

.duel-region {
  display: block;
  font-size: 0.65rem;
  color: #64748b;
}

.duel-vs {
  text-align: center;
  color: #6366f1;
  font-weight: 800;
  font-size: 1.1rem;
  line-height: 1.2;
}

.duel-vs small {
  display: block;
  font-size: 0.6rem;
  font-weight: 500;
  color: #64748b;
}
</style>